import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MAX_AGENTS = 3;

// Only the Claude models are wired up end to end. The rest are still listed so
// the roadmap is visible, but they cannot be picked — change this predicate
// (not the individual call sites) as other providers come online.
const SUPPORTED_MODEL_PREFIX = "claude";
// Claude models held back for now despite matching the prefix. Remove a key
// here to make it selectable again — nothing else needs to change.
const DISABLED_MODEL_KEYS = ["claude-opus-5"];
const isModelSelectable = (modelKey) => {
    const key = String(modelKey || "").toLowerCase();
    if (DISABLED_MODEL_KEYS.includes(key)) return false;
    return key.startsWith(SUPPORTED_MODEL_PREFIX);
};

// `locked`: the phase's agents are read-only (abstract stage after full-text
// screening has started). Nothing can be added, removed or edited.
const AiModelConfiguration = ({ phaseKey, value, onChange, availableModels, locked = false }) => {
    const models = useMemo(
        () => (Array.isArray(availableModels) ? availableModels : []),
        [availableModels]
    );
    const defaultModel = "claude-sonnet-4-6";
    const modelKeySet = useMemo(
        () => new Set(models.map((m) => m.model_key)),
        [models]
    );

    const [openAgentId, setOpenAgentId] = useState(null);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });
    const triggerRefs = useRef({});
    const panelRefs = useRef({});

    const openDropdown = (id) => {
        const btn = triggerRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < 220;
            if (openUpward) {
                const pageHeight = document.documentElement.scrollHeight;
                setPanelPos({
                    top: null,
                    bottom: pageHeight - (rect.top + window.scrollY) + 6,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                });
            } else {
                setPanelPos({
                    top: rect.bottom + window.scrollY + 6,
                    bottom: null,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                });
            }
        }
        setOpenAgentId(id);
    };

    const toggleDropdown = (id) => {
        const agent = agents.find((a) => a.id === id);
        if (locked || agent?.saved) return;
        if (openAgentId === id) setOpenAgentId(null);
        else openDropdown(id);
    };

    // Close on outside click 
    useEffect(() => {
        if (openAgentId == null) return;
        const handleClickOutside = (event) => {
            const trigger = triggerRefs.current[openAgentId];
            const panel = panelRefs.current[openAgentId];
            if (
                (trigger && trigger.contains(event.target)) ||
                (panel && panel.contains(event.target))
            ) {
                return;
            }
            setOpenAgentId(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openAgentId]);

    useEffect(() => {
        if (openAgentId == null) return;
        const handleScrollOrResize = (event) => {
            const panel = panelRefs.current[openAgentId];
            if (panel && event.target && panel.contains(event.target)) return;
            setOpenAgentId(null);
        };
        window.addEventListener("scroll", handleScrollOrResize, true);
        window.addEventListener("resize", handleScrollOrResize);
        return () => {
            window.removeEventListener("scroll", handleScrollOrResize, true);
            window.removeEventListener("resize", handleScrollOrResize);
        };
    }, [openAgentId]);

    const normalized = useMemo(() => {
        // A saved agent keeps whatever it ran with, even if that model is no
        // longer selectable — rewriting it would misreport what the project
        // actually used. Agents still being edited fall back to the default.
        const resolveModel = (m, saved) => {
            const key = m ? String(m) : "";
            if (!modelKeySet.has(key)) return defaultModel;
            if (!saved && !isModelSelectable(key)) return defaultModel;
            return key;
        };

        if (value?.agents && Array.isArray(value.agents)) {
        const agents = value.agents.map((a, idx) => ({
                id: a?.id ?? idx + 1,
                model: resolveModel(a?.model, a?.saved === true),
                temperature:
                    typeof a?.temperature === "number"
                        ? a.temperature
                        : Number.isFinite(parseFloat(a?.temperature))
                            ? parseFloat(a.temperature)
                            : 0.2,
                saved: a?.saved === true,
            }));

            const default_agent_id =
                value?.default_agent_id != null
                    ? value.default_agent_id
                    : agents.length
                        ? agents[0].id
                        : 1;

            return { agents, default_agent_id };
        }

        const derivedTemp = Number.isFinite(parseFloat(value?.temperature))
            ? parseFloat(value.temperature)
            : 0.2;

        return {
            agents: [{ id: 1, model: resolveModel(value?.model, false), temperature: derivedTemp }],
            default_agent_id: 1,
        };
    }, [value, modelKeySet, defaultModel]);

    const agents = normalized.agents || [];
    const defaultAgentId = normalized.default_agent_id;
    const isMaxAgentsReached = agents.length >= MAX_AGENTS;

    const handleAddAgent = () => {
        if (locked || isMaxAgentsReached) return;

        const nextId = agents.length ? Math.max(...agents.map((a) => a.id)) + 1 : 1;
        const nextAgents = [
            ...agents,
            { id: nextId, model: defaultModel, temperature: 0.2 },
        ];

        onChange({
            agents: nextAgents,
            default_agent_id: defaultAgentId ?? nextId,
        });
    };

    const handleRemoveAgent = (id) => {
        if (locked) return;
        const nextAgents = agents.filter((a) => a.id !== id);
        const nextDefault =
            id === defaultAgentId
                ? nextAgents.length
                    ? nextAgents[0].id
                    : null
                : defaultAgentId;

        onChange({
            agents: nextAgents,
            default_agent_id: nextDefault,
        });
    };

    const handleSetDefault = (id) => {
        onChange({
            agents,
            default_agent_id: id,
        });
    };

    const handleModelChange = (id, nextModel) => {
        if (locked || !isModelSelectable(nextModel)) return;
        const nextAgents = agents.map((a) =>
            a.id === id ? { ...a, model: nextModel } : a
        );
        onChange({
            agents: nextAgents,
            default_agent_id: defaultAgentId,
        });
    };

    // Models that reject sampling parameters are sent without a temperature,
    // so the slider would be a control with no effect. Keyed off the catalog
    // flag rather than the model name so a new model needs no code change.
    const modelAllowsTemperature = (modelKey) => {
        const entry = models.find((m) => m.model_key === modelKey);
        return entry ? entry.supports_sampling_params !== false : true;
    };

    const handleTempChange = (id, nextTemp) => {
        if (locked) return;
        const temp = Number.isFinite(parseFloat(nextTemp))
            ? Math.max(0, Math.min(1, parseFloat(nextTemp)))
            : 0;

        const nextAgents = agents.map((a) =>
            a.id === id ? { ...a, temperature: temp } : a
        );

        onChange({
            agents: nextAgents,
            default_agent_id: defaultAgentId,
        });
    };

    return (
        <div className="py-0 px-2">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <p className="ev-med-p-blue mb-0">
                        Screening Agents ({agents.length})
                    </p>
                    <p className="ev-xsmall-p-dark mb-0">
                        {phaseKey === "phase1"
                            ? "Abstract Screening Allocation"
                            : "Full-Text Screening Allocation"}
                    </p>
                </div>

                {/* Button + Max limit card */}
                {locked ? (
                    <div className="ev-alert-card-warning ev-radius-large px-2 py-1">
                        <p className="mb-0">
                            <i className="bi bi-lock me-1"></i>
                            Locked — full-text screening has started
                        </p>
                    </div>
                ) : (
                <div className="d-flex align-items-center gap-2">
                    {isMaxAgentsReached && (
                        <div className="ev-alert-card-warning ev-radius-large px-2 py-1">
                            <p className=" mb-0">Max. 3 LLMs allowed</p>
                        </div>
                    )}
                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-med avatar-tooltip"
                        onClick={handleAddAgent}
                        disabled={isMaxAgentsReached}
                        data-tooltip={isMaxAgentsReached ? "Max. 3 LLMs allowed" : ""}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        <span className=" ev-bold-reg"> Add LLM Agent </span>
                    </button>
                </div>
                )}
            </div>

            {/* Table header */}
            <div className="row ev-small-p-blue ev-bold-reg mb-1">
                <div className="ev-reg-p-dark-blue ev-bold-med col-2">Agent</div>
                <div className="ev-reg-p-dark-blue ev-bold-med col-3">Model</div>
                <div className="ev-reg-p-dark-blue ev-bold-med col-5">Temperature</div>
                <div className="ev-reg-p-dark-blue ev-bold-med col-2 text-end"> </div>
            </div>

            {/* Rows */}
            <div className="scrollable-element vh-35">
                <table className="table-custom1 overflow-hidden w-100 table-sm align-middle">
                    <tbody>
                        {agents.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="text-center">
                                    <p className="ev-small-p-dark mt-2 mb-2 py-1">
                                        No agents added yet.
                                    </p>
                                </td>
                            </tr>
                        ) : (
                            agents.map((a, idx) => {
                                const isOpen = openAgentId === a.id;
                                return (
                                    <tr key={a.id}>
                                        {/* Agent # */}
                                        <td className="align-middle py-2 px-2 w-15">
                                            <span className="ev-reg-p-dark">{idx + 1}</span>
                                        </td>

                                        {/* Model */}
                                        <td className="align-middle py-2 px-2 w-25">
                                            <button
                                                ref={(el) => { triggerRefs.current[a.id] = el; }}
                                                type="button"
                                                className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg w-50 d-flex align-items-center justify-content-between"
                                                onClick={() => toggleDropdown(a.id)}
                                                disabled={locked || a.saved || models.length === 0}
                                                title={
                                                    locked
                                                        ? "Abstract screening is locked — full-text screening has started"
                                                        : a.saved ? "Model cannot be changed after saving" : ""
                                                }
                                            >
                                                <span className="d-inline-flex align-items-center">
                                                    {a.model || "Select model"}
                                                </span>
                                                {!a.saved && !locked && (
                                                    <i className={`bi bi-chevron-${isOpen ? "up" : "down"} ms-1`}></i>
                                                )}
                                            </button>
                                        </td>

                                        {/* Temperature */}
                                        <td className="align-middle py-2 px-2 w-45">
                                            {!modelAllowsTemperature(a.model) ? (
                                                <span className="ev-xsmall-p-dark">
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    This model doesn't take a temperature — guide it
                                                    through the screening criteria instead.
                                                </span>
                                            ) : (
                                            <div className="d-flex align-items-center gap-3">
                                                <span className="ev-btn-blue-fill-round ev-btn-xsmall ev-radius-large pe">
                                                    Focused
                                                </span>

                                                <span className="ev-xsmall-p-dark ev-bold-med">0</span>

                                                <div className="position-relative flex-grow-1">
                                                    <span
                                                        className="ev-message-card6 ev-small-p-blue ev-bold-reg position-absolute ev-radius-reg"
                                                        style={{
                                                            left: `${a.temperature * 100}%`,
                                                            transform: "translateX(-50%)",
                                                            top: "-10px",
                                                            whiteSpace: "nowrap",
                                                            pointerEvents: "none",
                                                        }}
                                                    >
                                                        {a.temperature.toFixed(2)}
                                                    </span>
                                                    <input
                                                        type="range"
                                                        className="ev-range-slider"
                                                        min="0"
                                                        max="1"
                                                        step="0.01"
                                                        value={a.temperature}
                                                        disabled={locked}
                                                        style={{
                                                            background: `linear-gradient(to right, var(--color9) ${a.temperature * 100}%, var(--color8) ${a.temperature * 100}%)`,
                                                        }}
                                                        onChange={(e) => handleTempChange(a.id, e.target.value)}
                                                    />
                                                </div>

                                                <span className="ev-xsmall-p-dark ev-bold-med">1</span>

                                                <span className="ev-btn-blue-fill-round ev-btn-xsmall ev-radius-large">
                                                    Creative
                                                </span>
                                            </div>
                                            )}
                                        </td>

                                        {/* Action */}
                                        <td className="align-middle py-2 px-2 w-15 text-end">
                                            {!locked && agents.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                                                    onClick={() => handleRemoveAgent(a.id)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* dropdown panel */}
            {openAgentId != null && (() => {
                const a = agents.find((ag) => ag.id === openAgentId);
                if (!a) return null;
                return createPortal(
                    <div
                        ref={(el) => { panelRefs.current[openAgentId] = el; }}
                        className="ev-card-3 min-w-200 p-0"
                        style={{
                            position: "absolute",
                            ...(panelPos.bottom != null
                                ? { bottom: panelPos.bottom }
                                : { top: panelPos.top }
                            ),
                            left: panelPos.left,
                            // The scroll moved to the inner element so this one
                            // can clip it: a square scrollbar track painted on a
                            // 15px-radius card escaped the rounded corners.
                            overflow: "hidden",
                            zIndex: 9999,
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                            border: "1px solid var(--color1)",
                        }}
                    >
                      <div className="vh-15 scrollable-element pb-1 px-2 pt-2"
                           style={{ overflowY: "auto" }}>
                        {models.length === 0 && (
                            <p className="ev-small-p-dark text-center mt-2 mb-2">
                                No models available
                            </p>
                        )}
                        {models.map((m) => {
                            const isSelected = m.model_key === a.model;
                            const selectable = isModelSelectable(m.model_key);
                            return (
                                <button
                                    key={m.model_key}
                                    type="button"
                                    disabled={!selectable}
                                    className={`ev-btn-small w-100 d-flex align-items-center justify-content-between ev-radius-small mb-1 border-0 ${isSelected
                                        ? "ev-btn-blue-fill-round"
                                        : "ev-btn-light-fill"
                                        }${selectable ? "" : " opacity-50"}`}
                                    onClick={() => {
                                        if (!selectable) return;
                                        handleModelChange(a.id, m.model_key);
                                        setOpenAgentId(null);
                                    }}
                                >
                                    <span>{m.model_key}</span>
                                    {isSelected && <i className="bi bi-check2 ms-2"></i>}
                                </button>
                            );
                        })}
                      </div>
                    </div>,
                    document.body
                );
            })()}
        </div>
    );
};

export default AiModelConfiguration;